import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminPendingDisputes } from '../services/api';
import DisputeResolveModal, { ResolveResult } from './DisputeResolveModal';
import { reasonLabel } from '../constants/disputes';

const ADMIN_USER_ID = 110;

export interface PendingDispute {
  dispute_id: number;
  created_at: string;
  quote_request_id: number;
  request_title: string;
  homeowner_user_id: number;
  homeowner_name: string;
  homeowner_email: string;
  business_user_id: number;
  business_name: string;
  business_email: string;
  reason_category: string;
  description: string;
  status: string;
}

const formatFiledAt = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const datePart = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${datePart} at ${timePart}`;
};

const AdminDisputes: React.FC = () => {
  const navigate = useNavigate();
  const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!) : null;
  // Advisory client gate (matches backend isAdminUser = is_admin || id===110).
  const isAdmin = !!user && (user.is_admin === true || user.id === ADMIN_USER_ID);

  const [disputes, setDisputes] = useState<PendingDispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<PendingDispute | null>(null);

  const fetchQueue = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getAdminPendingDisputes();
      setDisputes(res.data.disputes || []);
    } catch (err: any) {
      setError('Failed to load disputes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Inline admin guard (matches AdminDashboard): must be the admin account, else bounce home.
  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On successful resolution we already have the result — drop the row, no re-fetch.
  const handleResolved = (result: ResolveResult) => {
    setDisputes((prev) => prev.filter((d) => d.dispute_id !== result.dispute_id));
    setSelected(null);
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <button style={styles.backLink} onClick={() => navigate('/admin')}>← Admin</button>

        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <h1 style={styles.title}>Disputes</h1>
            {!loading && !error && (
              <span style={disputes.length > 0 ? styles.pendingChip : styles.pendingChipZero}>
                {disputes.length} pending
              </span>
            )}
          </div>
          <button style={styles.refreshBtn} onClick={fetchQueue} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh queue'}
          </button>
        </div>

        {loading && <div style={styles.stateBox}>Loading disputes…</div>}

        {!loading && error && (
          <div style={styles.errorBox}>
            {error}
            <button style={styles.retryBtn} onClick={fetchQueue}>Retry</button>
          </div>
        )}

        {!loading && !error && disputes.length === 0 && (
          <div style={styles.emptyBox}>No pending disputes. ✓</div>
        )}

        {!loading && !error && disputes.length > 0 && (
          <div style={styles.list}>
            {disputes.map((d) => (
              <div key={d.dispute_id} style={styles.card}>
                <div style={styles.cardTopRow}>
                  <span style={styles.reasonBadge}>{reasonLabel(d.reason_category)}</span>
                  <span style={styles.filedAt}>{formatFiledAt(d.created_at)}</span>
                </div>

                <div style={styles.parties}>
                  <div style={styles.party}>
                    <div style={styles.partyLabel}>Homeowner</div>
                    <div style={styles.partyName}>{d.homeowner_name}</div>
                    <div style={styles.partyEmail}>{d.homeowner_email}</div>
                  </div>
                  <div style={styles.party}>
                    <div style={styles.partyLabel}>Contractor</div>
                    <div style={styles.partyName}>{d.business_name}</div>
                    <div style={styles.partyEmail}>{d.business_email}</div>
                  </div>
                </div>

                <div style={styles.requestRow}>
                  <span style={styles.requestLabel}>Request:</span> {d.request_title}
                </div>

                <p style={styles.description}>{d.description}</p>

                <div style={styles.cardActions}>
                  <button style={styles.upholdBtn} onClick={() => setSelected(d)}>Uphold</button>
                  <button style={styles.dismissBtn} onClick={() => setSelected(d)}>Dismiss</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <DisputeResolveModal
          dispute={selected}
          isOpen={!!selected}
          onClose={() => setSelected(null)}
          onResolved={handleResolved}
        />
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: { minHeight: '100vh', background: '#f5f6fa', padding: '24px 16px' },
  container: { maxWidth: 900, margin: '0 auto' },
  backLink: { background: 'none', border: 'none', color: '#64748b', fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 12 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  title: { fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0 },
  pendingChip: { padding: '4px 12px', borderRadius: 999, background: '#fffbeb', border: '1px solid #fcd34d', color: '#b45309', fontSize: 13, fontWeight: 700 },
  pendingChipZero: { padding: '4px 12px', borderRadius: 999, background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#64748b', fontSize: 13, fontWeight: 700 },
  refreshBtn: { padding: '8px 16px', background: '#fff', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#334155' },
  stateBox: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 40, textAlign: 'center', color: '#64748b', fontSize: 15 },
  emptyBox: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 56, textAlign: 'center', color: '#15803d', fontSize: 18, fontWeight: 700 },
  errorBox: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: 24, textAlign: 'center', color: '#dc2626', fontSize: 15, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 },
  retryBtn: { padding: '8px 16px', background: '#fff', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#dc2626' },
  list: { display: 'flex', flexDirection: 'column', gap: 16 },
  card: { background: '#fff', border: '1px solid #e2e8f0', borderLeft: '4px solid #f59e0b', borderRadius: 12, padding: 20 },
  cardTopRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  reasonBadge: { padding: '4px 12px', borderRadius: 999, background: '#fff7ed', border: '1px solid #fed7aa', color: '#c2410c', fontSize: 13, fontWeight: 700 },
  filedAt: { fontSize: 13, color: '#94a3b8' },
  parties: { display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 14 },
  party: { minWidth: 220, flex: 1 },
  partyLabel: { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 },
  partyName: { fontSize: 15, fontWeight: 700, color: '#0f172a' },
  partyEmail: { fontSize: 13, color: '#64748b', wordBreak: 'break-word' },
  requestRow: { fontSize: 14, color: '#334155', marginBottom: 10 },
  requestLabel: { fontWeight: 700, color: '#0f172a' },
  description: { fontSize: 14, color: '#334155', lineHeight: 1.6, margin: '0 0 18px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#f8fafc', borderRadius: 8, padding: 12 },
  cardActions: { display: 'flex', gap: 10 },
  upholdBtn: { padding: '9px 22px', borderRadius: 8, border: '1px solid #f59e0b', background: 'linear-gradient(135deg, #ef4444, #f59e0b)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  dismissBtn: { padding: '9px 22px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f1f5f9', color: '#334155', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
};

export default AdminDisputes;
